import * as React from "react";
import { ComponentType, FC, useCallback } from "react";
import {
    Location,
    NavigateFunction,
    To,
    useLocation,
    useMatch,
    useMatches,
    useParams,
} from "react-router";
import useNavigate from "src/hooks/useNavigate";

interface IRouterMatch<TParams> {
    params: TParams;
}

interface IRouterHistory {}

export interface IRouterProps<TParams> {
    match: IRouterMatch<TParams>;
    location: Location;
    history: IRouterHistory;
}

export interface IRouterActions {
    navigate: (to: To) => void;
    navigateReplace: (to: To) => void;
}
type UnrouterMeProps<
    TParams = any,
    TProps extends IRouterProps<TParams> = IRouterProps<TParams>
> = Omit<
    TProps,
    "match" | "location" | "history" | "navigate" | "navigateReplace"
>;

const RouterHoc = <
    TParams extends object = Record<string, string>,
    TProps extends IRouterProps<TParams> = IRouterProps<TParams>
>(
    Component: ComponentType<TProps>
): FC<UnrouterMeProps<TParams, TProps & IRouterActions>> => {
    return (props: UnrouterMeProps<TProps>) => {
        const location = useLocation();
        const history: IRouterHistory = {};
        const params = useParams();
        const match: IRouterMatch<TParams> = {
            params: params as TParams,
        };

        const navigate = useNavigate();

        const navigateReplace = useCallback(
            (to: To) => {
                navigate(to, { replace: true });
            },
            [navigate]
        );

        const allProps = {
            ...props,
            history: history,
            match: match,
            location: location,
            navigate,
            navigateReplace,
        } as TProps & IRouterActions;

        return <Component {...allProps} />;
    };
};

export default RouterHoc;
